import { useEffect, useState, type SetStateAction } from 'react'

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: SetStateAction<T>) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue))
        } catch (error) {
          console.error(
            `Error parsing localStorage value for key "${key}":`,
            error
          )
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  const setValue = (value: SetStateAction<T>) => {
    setStoredValue((prev) => {
      try {
        const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value
        window.localStorage.setItem(key, JSON.stringify(next))
        return next
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error)
        return prev
      }
    })
  }

  return [storedValue, setValue]
}
